import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
  NgZone
} from '@angular/core';
import {
  LMCubeSearchOpenLookupEvent,
  LMDefineEvent,
  LMFieldBaseComponent,
  LMOpenLookupEvent
} from '@field/lm-field-base.component';
import { LMModel } from '@form/model';
import { SohoLookupComponent } from 'ids-enterprise-ng';
import { LMLookupDialogComponent } from './lm-lookup-dialog.component';
import { LMSpecService } from '@services/lm-spec.service';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import { first } from 'rxjs/operators';
import { AppConstants, LMDialogButtonKey } from '@utils/app.constants';
import { LMLookupSelectionEvent } from './lm-lookup-selection.event';
import { LMFieldPickerService } from '@modal/field-picker/services';
import { LMKeyboardShortcutService, LMKeyType } from '@services/lm-keyboard-shortcut.service';
import { LMContextService } from '@services/index';
import { LMSelectDialogService, LMSelectResponse } from '@modal/index';
import KeyPressEvent = JQuery.KeyPressEvent;
import { LMFieldPickerSelectOptions, LMFieldPickerSelectResponse } from '@modal/field-picker/lm-field-picker';
import { Observable } from 'rxjs';
import { LMContextUtilService } from "@app/utils";

enum LMLookupTypeDisplayEnum {
  PLAIN = 0,
  MASK,
  TEXT,
  TAG
}

enum LMLookupDialogResult {
  CANCEL = 'CANCEL',
  OK = 'OK',
  TOOLBAREVT = 'TOOLBAREVT'
}

interface OnLookupFieldResult {
  fromFieldPicker: boolean;
  fullFieldName: string;
}

@Component({
  selector:    'lm-lookup',
  templateUrl: './lm-lookup.component.html',
  styleUrls:   [ 'lm-lookup.component.scss' ]
})
export class LMLookupComponent extends LMFieldBaseComponent implements OnInit, AfterViewInit, OnDestroy {

  @HostBinding('attr.tabindex') tabIndex = this.lmParagraphLayoutInfo && this.lmParagraphLayoutInfo.isCompoundField ? '0' : null;

  @HostBinding('attr.data-automation-id') get dataFieldAutomationId() {
    return `lm-${this.fieldAutomationId}-field`;
  }

  /**
   * The 'dialogPlaceholder' is where the reference dialog component will be
   * parented when it is instantiated.
   *
   * This can be the ViewContainerRef of this component, or another component.
   */
  @ViewChild('dialogPlaceholder', { read: ViewContainerRef, static: true }) placeholder: ViewContainerRef;
  @ViewChild(SohoLookupComponent) sohoLookup: SohoLookupComponent;

  @Input() layoutObject: LMLookupLayout;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;

  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();

  @HostBinding('attr.class') hostClass = 'field';

  @HostListener('keyup', [ '$event' ])
  onKeyPress(event: KeyPressEvent) {
    if (!this.modelField.isBlank) {
      const key = this.lmKeyboardShortcutService.onKeyDown(event);
      if (key === LMKeyType.DEFINE) {
        this.fieldEvent.emit(new LMDefineEvent(new Event('click'), this.bindId, this.model.getData()));
      }
    }
  }

  /**
   * The interface to an instantiated instance of the dialog component.
   */
  // private dialog: SohoModalDialogRef<any>;

  public displayType: LMLookupTypeDisplayEnum;
  public layoutDisplayType;
  public showCompactDialogButton = false;
  public launchButtonTooltip: string;
  public showBarCodeScanOptions = false;
  public showBarCodeOnlyButton = false;
  public iconDisplay = 'search-list';

  private isAlert = false;

  private sourceField: string;
  private targetField: string;
  private lookupSelectionEvent: LMLookupSelectionEvent;
  private lookupSelectionSubscription;

  constructor(
    private   element: ElementRef,
    protected lmFieldServiceAdapter: LMFieldServiceAdapter,
    private   lmSpecService: LMSpecService,
    private lmFieldPickerService: LMFieldPickerService,
    private lmContextService: LMContextService,
    private lmKeyboardShortcutService: LMKeyboardShortcutService,
    protected lmContextUtilService: LMContextUtilService,
    private lmSelectDialogService: LMSelectDialogService
  ) {
    super(lmFieldServiceAdapter);
    this.layoutDisplayType = LMLookupTypeDisplayEnum;
  }

  // events
  ngOnInit() {
    super.ngOnInit();
    this.initialize(this.layoutObject, this.specFields, this.model);
    this.launchButtonTooltip = Soho.Locale.translate('Launch');

    if (this.specField) {
      this.setNumberFormat();
      this.setSearchFieldNumberFormat();
      this.setSize();
      this.setFieldClasses();

      this.sourceField = this.specField.selectFieldId || this.bindId;
      this.targetField = this.bindId;
    }

    if (this.displayAsTag && !this.isCompoundFieldMember) {
      this.displayType = this.layoutDisplayType.TAG;

    } else if (this.displayAsText && !this.isCompoundFieldMember) {
      this.displayType = this.layoutDisplayType.TEXT;

    } else {
      this.subscribeToInlineError();

      if (!this.hasMask) {
        this.displayType = this.layoutDisplayType.PLAIN;

      } else {
        this.displayType = this.layoutDisplayType.MASK;
        // if a lookup has a type of BCD or PERCENT, then will need to re-factor to have
        // same functionality for layoutDisplayType.MASK as lm-text.component
      }

      if (this.specField.compactFormat && !this.specField.compactFormatOnly) {
        this.showCompactDialogButton = true;
      }

      if (this.field.scannable && this.lmContextService.insideInforGo()) {
        this.iconDisplay = 'none';
        if (this.specField.definable) {
          this.showBarCodeScanOptions = true;
        } else {
          this.iconDisplay = 'barcode';
          this.showBarCodeOnlyButton = true;
        }
      }
    }

    if (this.isTimezoneField) {
      this.previousTimeZone = this.modelField.value;
    }
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    // if the lookup is a compound field member, listen to the
    // mouse down event on the triggering span and prevent this event from bubbling up.
    // This is because of intermittent timing issues where the blur event out of the
    // lookup's input would collapse hide the trigger span before the mouseup/click
    // event could launch the lookup's modal
    setTimeout(() => {
      if (this.element.nativeElement && this.isCompoundFieldMember && this.displayType !== this.layoutDisplayType.TEXT) {
        this.lmFieldServiceAdapter.renderer2.listen(this.element.nativeElement.querySelector('#' + this.id + ' ~ button'), 'mousedown', (event: Event) => {
          event.preventDefault();
        });
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  // Will be used when Contextual panel is used in the place of a dialog (See LMCLIENT-18522 for details.)
  // public get isModal(): boolean {
  //   return this.model.formSpec.container === 'contextualpanel';
  // }

  public onNgModelChange(value) {
    if (this.model.isCubeSearchForm) {
      // Special case for cubes -- multi selection is not allowed.
      // And since the value, irrespective of the type, is changed to
      // '/' separated string of ontological key values + selected value,
      // converting toDataValue will yield undesirable results.
      // Eg: Scenario in GLTotal is numeric, but the lookup value is 'SCB/1'
      this.modelField.value = value;
      return;
    }

    // if a lookup has a type of BCD or PERCENT, then will need to re-factor to have
    // same functionality for layoutDisplayType.MASK as lm-text.component
    if (this.transformOptions.allowMultiSelections && this.onSearchForm) {
      let valueString = value;

      if (this.isUpperCase) {
        valueString = valueString.toUpperCase();
      }

      if (Array.isArray(valueString) && this.transformOptions.allowMultiSelections) {
        const formattedArray = [];
        value.map((val) => {
          if (val.indexOf(AppConstants.MULTI_SEARCH_TERM_DELIMITER) > -1) {
            formattedArray.push('"' + val + '"');
          } else {
            formattedArray.push(val);
          }
        });
        valueString = formattedArray.join(AppConstants.MULTI_SEARCH_TERM_DELIMITER);
      }
      const allowZero = this.specField.type === AppConstants.BCD ||
        this.specField.type === AppConstants.PERCENT ||
        this.specField.type === AppConstants.NUMERIC;
      const multiValue = this.lmFieldServiceAdapter.lmFormatUtilService.getMultipleFilterFieldValue(valueString, this.specField.type, this.specField.decSize, this.modelField.precision, allowZero);
      this.modelField.enteredFilterValue = multiValue.enteredFilterValue;
      this.modelField.value = multiValue.formattedFilterValue;
    } else {
      this.modelField.toDataValue(value, this.transformOptions);
    }
    // }

    if (this.isTimezoneField) {
      const previousTimeZone = this.previousTimeZone || this.modelField?.originalValue;
      const timeZone = value;
      this.model.timeZoneChanged({ bindId: this.bindId, timeZone, previousTimeZone });
      this.previousTimeZone = timeZone;
    }
  }

  contextMenuClicked = ($event) => {

    if ($event.args) {
      const menuElement = $event.args[ 0 ];
      const menuId = menuElement.getAttribute('id');

      if (menuId === 'SelectEvent') {
        this.onLookupClick($event, null);
      } else if (menuId === 'ScanEvent') {
        this.onBarCodeClick($event, null);
      } else {
        this.onContextMenuEvent($event);
      }
    } else {
      this.onContextMenuEvent($event);
    }
  };

  onLookupField = (data: any) => {
    if (data.fromFieldPicker) {
      const lookupFieldResult = <OnLookupFieldResult> data;
      return lookupFieldResult.fullFieldName;
    }
    const dataView: LMDataView = <LMDataView> data;
    return dataView.fields[ this.sourceField ].value;
  };

  // eslint-disable-next-line no-unused-vars
  onBarCodeClick = (e, lookup) => {
    this.lmContextUtilService.showQRCodeScanner().subscribe((response: LMBarCodeData) => {
      if (response?.data) {
        // sohoLookupComponent requires a data property when setting a value
        const scanResults = response.data;
        this.sohoLookup.setValue([
          {
            data: {
              fromFieldPicker: true,
              scanResults
            }
          } as any
        ]);
        this.model.clearFieldError(this.bindId);
        this.modelField.value = scanResults;
        if (this.transformOptions.allowMultiSelections && this.onSearchForm) {
          this.modelField.enteredFilterValue = scanResults;
        }
      }
    });
  };

  // eslint-disable-next-line no-unused-vars
  onLookupClick = (e, lookup) => {

    if (this.showBarCodeOnlyButton) {
      this.onBarCodeClick(e, lookup);
      return;
    }

    this.busyIndicator = true;
    if (this.model.isCubeSearchForm) {
      // The LMOpenLookup event handler will implement custom behavior for cube views.
      this.fieldEvent.emit(new LMCubeSearchOpenLookupEvent(e, this));
      this.busyIndicator = false;
      return;
    }

    this.fieldEvent.emit(new LMOpenLookupEvent(e, this.bindId));

    if (this.layoutObject.lplFieldInfo) {
      this.showFieldPickerDialog(e);
      return;
    }

    if (this.lmContextService.useSingleSelectList()) {
      this.showSelectListDialog(e);
    } else {
      this.showClassicSelectListDialog(e);
    }
  };

  private showFieldPickerDialog(e: Event) {
    this.getFieldPickerBusinessClass().subscribe(businessClass => {
      const options: LMFieldPickerSelectOptions = {
        view:                  this.lmFieldPickerService.createFieldPickerView(this.layoutObject.lplFieldInfo),
        businessClass,
        enableOperatorBuilder: this.lmFieldPickerService.fieldPickerOperatorsEnabled(this.layoutObject.lplFieldInfo),
        includeAuditFields:    this.lmFieldPickerService.includesAuditFields(this.layoutObject.lplFieldInfo),
      };
      this.lmFieldPickerService.openFieldPickerDialog(options, this.layoutObject.selectListUrl, this.placeHolder).subscribe((response: LMFieldPickerSelectResponse) => {
        if (response) {
          this.processFieldPickerResult(e, response);
        }
        this.busyIndicator = false;
      });
    });
  }

  private getFieldPickerBusinessClass(): Observable<string>  {
    return new Observable<string>(observer => {
      // LMRK bug workaround.  LMRK does not fill out a derived field in a create state, must always get the value.
      // https://jira.lawson.com/browse/LMCLIENT-39879
      if (this.layoutObject.lplFieldInfo.isRelatedField) {
        const formUrl = this.lmFieldServiceAdapter.lmUrlUtilService.buildFormRequestUrl(this.model.formUrlInfo);
        this.lmFieldPickerService.getBusinessClassForRelatedField(this.model.getData(), this.bindId, this.model.formResourceId, formUrl).subscribe(response => {
          if (response.status === 'COMPLETED') {
            observer.next(response.busClassName);
          } else {
            observer.next('');
          }
          observer.complete();
        });
      } else {
        observer.next(this.layoutObject.lplFieldInfo.busClassName);
        observer.complete();
      }
    });
  }

  private showSelectListDialog(e: Event) {
    this.busyIndicator = false;
    const selectListUrl = this.lmFieldServiceAdapter.lmUrlUtilService.buildSelectListUrl(this.model.formUrlInfo, this.layoutObject.selectListInfo);
    if (!this.lmFieldServiceAdapter.lmContextService.useNewSpecUrls()) {
      // tslint:disable-next-line:max-line-length
      this.lmFieldServiceAdapter.lmUrlInfoBuilderService.compareSpecUrls(this.model.formUrlInfo, this.layoutObject.bindId + '.selectListUrl', (this.layoutObject as any).selectListUrl, selectListUrl);
    }

    this.lmSpecService.getListSpec(selectListUrl).subscribe((listSpecResponse: LMListSpecResponse) => {
      this.lmFieldServiceAdapter.lmLoggerService.log([ 'LMLookupComponent', 'getListSpec() completed', listSpecResponse ]);

      const listUrlInfo = listSpecResponse.listUrlInfo;
      const listSpec = listSpecResponse.listSpec;

      // If the list spec has search form, it is either displayed initially or the user will click on search icon to open up.
      // In either case, do not set the height of the modal. The search form and the list together should be in auto height.
      // Also in the case of tree view, do not want to set the modal with overflow:hidden. As the nodes are expanded, we should be able to scroll.
      const className = (listSpec.busListSpec.searchForm === undefined && !listSpec.busListSpec.implementsTreeView) ? 'lm-lookup-modal' : '';

      const fieldHasNoVariables = (!this.specField.label.variables || this.specField.label.variables.length === 0);
      const labelValue = (this.specField.label.resourceValue !== ' ' || !fieldHasNoVariables)
        ? this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(this.specField.label, this.model, this.specFields)
        : '';
      const title = (labelValue !== '')
        ? this.lmFieldServiceAdapter.lmFormatUtilService.formatMessage(Soho.Locale.translate('SelectTitle'), labelValue)
        : Soho.Locale.translate('Select');

      const formSpec = this.model.formSpec;
      const formUrlInfo = this.model.formUrlInfo;
      const selectRequestData: SelectRequestData = {
        viewType: formSpec.selectViewType ? formSpec.selectViewType : 'form',
        viewName: this.lmFieldServiceAdapter.lmFormSpecUtilService.formName(formSpec),
        dataView: this.getSearchFormValidatedDataView(),
        allowMultiSelections: !!this.transformOptions.allowMultiSelections,
        maintainSelectedValues: !!this.transformOptions.maintainSelectedValues,
        userFolder: this.layoutObject.selectListInfo.userFolder,
      };

      if (selectRequestData.allowMultiSelections && this.lmContextService.enableMaintainingSelectedValues()) {
        selectRequestData.maintainSelectedValues = true;
        const editTitle = (labelValue !== '')
          ? this.lmFieldServiceAdapter.lmFormatUtilService.formatMessage(Soho.Locale.translate('EditTitle'), labelValue)
          : Soho.Locale.translate('Edit');
        const selectedValues = this.getInputElement()?.value;
        const selectedValuesData: SelectedValuesData = {
          selectedValues,
          fieldId: this.sourceField,
          type: this.specField.type,
          decSize: this.specField.decSize,
          precision: this.modelField.precision,
          selectTitle: title,
          editTitle
        };
        selectRequestData.selectedValuesData = selectedValuesData;
      }

      if (formUrlInfo.pfActionName) {
        selectRequestData.pfActionName = formUrlInfo.pfActionName;
      } else if (this.model.action) {
        selectRequestData.actionName = this.model.action;
      }
      if (formUrlInfo.navigationContext) {
        selectRequestData.navigationContext = formUrlInfo.navigationContext;
      }
      if (this.model.isSearchForm) {
        selectRequestData.onSearchForm = true;
      }

      this.lmSelectDialogService.showSelectListDialog({
        listUrlInfo,
        listSpec,
        title,
        sourceField: this.sourceField,
        className,
        selectRequestData
      }).pipe(
        first()) // causes the subscription to complete after its messaged the first time
        .subscribe((selectResponse: LMSelectResponse) => {
          this.lmFieldServiceAdapter.lmLoggerService.log([ 'LMLookupComponent', 'showSelectListDialog()', selectResponse ]);
          if (selectResponse?.status === AppConstants.STATUS_COMPLETED && selectResponse.dataViews) {
            // clear any errors
            this.model.clearFieldError(this.targetField);

            // safety: need to be outside of the AngularZone
            if (!NgZone.isInAngularZone()) {
              this.sohoLookup.setValue(selectResponse.rows);
            } else {
              this.lmFieldServiceAdapter.ngZone.runOutsideAngular(() => {
                this.sohoLookup.setValue(selectResponse.rows);
              });
            }

            let value = '';
            if (this.transformOptions.maintainSelectedValues && this.onSearchForm) {
              selectResponse.multiSelectedValues.forEach((item, index, array) => {
                let selectedValue = '' + item.formattedValue;
                selectedValue = selectedValue.includes(AppConstants.MULTI_SEARCH_TERM_DELIMITER) ? `"${selectedValue}"` : selectedValue;
                const delimiter = (index === array.length -1) ? '' : AppConstants.MULTI_SEARCH_TERM_DELIMITER;
                value += `${selectedValue}${delimiter}`;
              });
              const multiValue = this.lmFieldServiceAdapter.lmFormatUtilService.getMultipleFilterFieldValue(value, this.specField.type, this.modelField.precision, this.specField.decSize);
              this.modelField.enteredFilterValue = multiValue.enteredFilterValue;
              this.modelField.value = multiValue.formattedFilterValue;
            } else if (this.transformOptions.allowMultiSelections && this.onSearchForm) {
              // @ts-ignore don't have correct typing out of soho
              const selectedValues = selectResponse.rows.length > 1 ? (selectResponse.rows.values as Array<any>) : [ selectResponse.rows.values ];
              selectedValues.forEach((selectedValue, index, array) => {
                selectedValue = selectedValue.includes(AppConstants.MULTI_SEARCH_TERM_DELIMITER) ? `"${selectedValue}"` : selectedValue;
                const delimiter = (index === array.length -1) ? '' : AppConstants.MULTI_SEARCH_TERM_DELIMITER;
                value += `${selectedValue}${delimiter}`;
              });
              const multiValue = this.lmFieldServiceAdapter.lmFormatUtilService.getMultipleFilterFieldValue(value, this.specField.type, this.modelField.precision, this.specField.decSize);
              this.modelField.enteredFilterValue = multiValue.enteredFilterValue;
              this.modelField.value = multiValue.formattedFilterValue;
            } else {
              const dataView: LMDataView = selectResponse.dataViews[0];
              const fields = dataView.fields;
              if (fields.hasOwnProperty(this.sourceField) && !this.transformOptions.allowMultiSelections) {
                this.modelField.value = fields[this.sourceField].value;
              }
            }

            this.handleOnFieldChanged();
            this.fieldEvent.emit(new LMOpenLookupEvent(e, null));
          } else if (selectResponse?.status === AppConstants.STATUS_CANCEL) {
            this.getInputElement()?.focus();
          }
        }
        );
    },
    (error: any) => {
      this.busyIndicator = false;
      this.lmFieldServiceAdapter.lmLoggerService.log([ 'LMLookupComponent error', error ]);
    });
  }

  // TODO: Remove the following and its supporting methods once we now longer support classic lookup selection
  private showClassicSelectListDialog(e: Event) {
    const cancelButton = this.lmFieldServiceAdapter.lmUniqueIdService.uniqueId([ this.id, LMDialogButtonKey.CANCEL ]);
    const okButton = this.lmFieldServiceAdapter.lmUniqueIdService.uniqueId([ this.id, LMDialogButtonKey.OK ]);

    const buttonIds = {
      ok: okButton,
      cancel: cancelButton
    };

    const buttons: SohoModalButton[] = [ {
      id:    cancelButton,
      text:  Soho.Locale.translate(LMDialogButtonKey.CANCEL),
      // tslint:disable-next-line:no-shadowed-variable > shadowed variable e
      click: () => {
        this.dialog.close(LMLookupDialogResult.CANCEL);
      }
    }, {
      id:        okButton,
      text:      Soho.Locale.translate(LMDialogButtonKey.OK),
      isDefault: true,
      // tslint:disable-next-line:no-shadowed-variable > shadowed variable e
      click:     () => {
        // safety: need to be in the AngularZone
        if (NgZone.isInAngularZone()) {
          this.handleSelection();

        } else {
          this.lmFieldServiceAdapter.ngZone.run(() => {
            this.handleSelection();
          });
        }
      }
    } ];

    const selectListUrl = this.lmFieldServiceAdapter.lmUrlUtilService.buildSelectListUrl(this.model.formUrlInfo, this.layoutObject.selectListInfo);
    if (!this.lmFieldServiceAdapter.lmContextService.useNewSpecUrls()) {
      // tslint:disable-next-line:max-line-length
      this.lmFieldServiceAdapter.lmUrlInfoBuilderService.compareSpecUrls(this.model.formUrlInfo, this.layoutObject.bindId + '.selectListUrl', (this.layoutObject as any).selectListUrl, selectListUrl);
    }

    this.lmSpecService.getListSpec(selectListUrl).subscribe((listSpecResponse: LMListSpecResponse) => {
      this.lmFieldServiceAdapter.lmLoggerService.log([ 'LMLookupComponent', 'listService.getListSpec()', 'Complete' ]);

      const listSpec = listSpecResponse.listSpec;
      const listUrlInfo = listSpecResponse.listUrlInfo;

      const fieldHasNoVariables = (!this.specField.label.variables || this.specField.label.variables.length === 0);
      const labelValue = (this.specField.label.resourceValue !== ' ' || !fieldHasNoVariables)
        ? this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(this.specField.label, this.model, this.specFields)
        : '';
      const title = (labelValue !== '')
        ? this.lmFieldServiceAdapter.lmFormatUtilService.formatMessage(Soho.Locale.translate('SelectTitle'), labelValue)
        : Soho.Locale.translate('Select');
      // If the list spec has search form, it is either displayed initially or the user will click on search icon to open up.
      // In either case, do not set the height of the modal. The search form and the list together should be in auto height.
      // Also in the case of tree view, do not want to set the modal with overflow:hidden. As the nodes are expanded, we should be able to scroll.
      const className = (listSpec.busListSpec.searchForm === undefined && !listSpec.busListSpec.implementsTreeView) ? 'lm-lookup-modal' : '';

      // we handle focus in field base, so don't need the controls to. Set noRefocus to true, was causing https://jira.lawson.com/browse/LMCLIENT-34141
      this.dialog = this.lmFieldServiceAdapter.sohoModalDialogService
        .modal<LMLookupDialogComponent>(LMLookupDialogComponent, this.placeholder, { fullsize: 'responsive', noRefocus: true })
        .buttons(buttons)
        .title(title)
        .isAlert(this.isAlert)
        .autoFocus(false)
        .cssClass(className)
        .beforeOpen(() => {
          this.busyIndicator = false;
          return true;
        })
        .open()
        .apply((lookupDialog: LMLookupDialogComponent) => {
          // LMCLIENT-22274, LMCLIENT-26674
          // safety: need to be in the AngularZone
          // // LMCLIENT-22274, LMCLIENT-26674
          // // safety: need to be in the AngularZone
          // if (NgZone.isInAngularZone()) {
          //   this.doApply(lookupDialog, listUrlInfo, listSpec, buttonIds);
          //
          // } else {
          //   this.lmFieldServiceAdapter.ngZone.run(() => {
          //     this.doApply(lookupDialog, listUrlInfo, listSpec, buttonIds);
          //   });
          // }
          this.lmFieldServiceAdapter.ngZone.run(() => {
            this.doApply(lookupDialog, listUrlInfo, listSpec, buttonIds);
          });
        })
        .afterClose(result => {
          // should already be in AngularZone
          if (result && result === LMLookupDialogResult.OK) {
            if (!this.modelField.fireInitialValue) {
              this.busyIndicator = false;
            }

            this.handleOnFieldChanged();
            this.fieldEvent.emit(new LMOpenLookupEvent(e, null));
          } else {
            // result can be undefined from the user hitting the escape key on the lookup dialog
            this.busyIndicator = false;
          }

          if (this.lookupSelectionSubscription) {
            this.lookupSelectionSubscription.unsubscribe();
            this.lookupSelectionSubscription = null;
          }

          this.lookupSelectionEvent = null;
          this.dialog = null;
        });
    },
    (error: any) => {
      this.busyIndicator = false;
      this.lmFieldServiceAdapter.lmLoggerService.log([ 'LMLookupDialog error', error ]);
    });
  }

  openCompactDialog(event) {
    this.onContextMenuEvent({ args: [ event.currentTarget ] });
  }

  private doApply(lookupDialog: LMLookupDialogComponent, listUrlInfo: LMListUrlInfo, listSpec: LMListSpec, buttonIds: {ok: string, cancel: string}) {
    const formUrlInfo = this.model.formUrlInfo;
    const formSpec = this.model.formSpec;
    const viewType = formSpec.selectViewType ? formSpec.selectViewType : 'form';

    lookupDialog.listUrlInfo = listUrlInfo;
    lookupDialog.listSpec = listSpec;
    lookupDialog.selectRequestData = {
      viewType,
      viewName: this.lmFieldServiceAdapter.lmFormSpecUtilService.formName(formSpec),
      dataView: this.getSearchFormValidatedDataView(),
    };
    lookupDialog.buttonIds = buttonIds;

    if (formUrlInfo.pfActionName) {
      lookupDialog.selectRequestData.pfActionName = formUrlInfo.pfActionName;
    } else if (this.model.action) {
      lookupDialog.selectRequestData.actionName = this.model.action;
    }

    if (formUrlInfo.navigationContext) {
      lookupDialog.selectRequestData.navigationContext = formUrlInfo.navigationContext;
    }

    lookupDialog.titleChanged.pipe(first()).subscribe(newTitle => {
      this.dialog.title(newTitle);
    });

    // need to listen for all occurrences as will get a row selection for a 'deselection'
    this.lookupSelectionSubscription =
        lookupDialog.lookupSelection.subscribe((event: LMLookupSelectionEvent) => {
          this.lookupSelectionEvent = event;
        });

    lookupDialog.navigatingToolbarEvent.pipe(first()).subscribe(() => {
      this.dialog.close(LMLookupDialogResult.TOOLBAREVT);
    });

    lookupDialog.triggerDetectChanges();
  }

  private handleSelection() {
    if (this.lookupSelectionEvent) {
      const result: Array<any> = this.lookupSelectionEvent.rows;
      if (result && Array.isArray(result) && result.length > 0) {
        // clear any errors
        this.model.clearFieldError(this.bindId);

        const dataView: LMDataView = result[ 0 ].data;
        const fields = dataView.fields;

        // special check here: if values are the same, then formControl.valueChange will not be fired,
        // so stop the busyIndicator as the subsequent fireInitialValueRule event will not be emitted
        // needs to occur before the setValue() below
        if (this.modelField.fireInitialValue
            && fields.hasOwnProperty(this.sourceField)
            && fields[ this.sourceField ].value === this.modelField.value) {
          this.busyIndicator = false;
        }

        // safety: need to be outside of the AngularZone
        if (!NgZone.isInAngularZone()) {
          this.sohoLookup.setValue(this.lookupSelectionEvent.rows);

        } else {
          this.lmFieldServiceAdapter.ngZone.runOutsideAngular(() => {
            this.sohoLookup.setValue(this.lookupSelectionEvent.rows);
          });
        }

        if (fields.hasOwnProperty(this.sourceField) && !this.transformOptions.allowMultiSelections) {
          this.modelField.value = fields[ this.sourceField ].value;
        }
      }
    }

    this.dialog.close(LMLookupDialogResult.OK);
  }

  private processFieldPickerResult(e: Event, response: LMFieldPickerSelectResponse): void {
    const fullFieldName = response.fieldName;
    // sohoLookupComponent requires a data property when setting a value
    this.sohoLookup.setValue([
      {
        data: {
          fromFieldPicker: true,
          fullFieldName
        }
      } as any
    ]);
    this.model.clearFieldError(this.bindId);
    this.modelField.value = fullFieldName;
    this.handleOnFieldChanged();
    this.fieldEvent.emit(new LMOpenLookupEvent(e, null));
  }
}
